import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpCacheInterceptorModule } from '@ngneat/cashew';
import { UserColorSchemeService } from '@y42/browser/user-color-scheme';
import { provideChatConfigService as provideChatConfigServiceV1 } from '@y42/collaboration/chat';
import { provideListViewQuickFilterKeys } from '@y42/core/list-view';
import { provideMockUserTrackingService } from '@y42/core/user-tracking/src/lib/testing';
import { DataLineageDiagramHydrationService } from '@y42/data-lineage/diagram';
import { APP_UX, provideEnvironment } from '@y42/environment';
import { ErrorHandlerModule } from '@y42/error-handling';
import {
  FeatureAggregationService,
  FeatureChildAggregationService,
  FeatureProvidersServiceInterface,
} from '@y42/feature';
import { FeatureFlagService } from '@y42/feature-flag';
import { provideGitDiffTree } from '@y42/feature/git';
import { AbstractJobCreatorResolverService } from '@y42/job';
import { providePushNotificationConfig } from '@y42/push-notification';
import { ToastModule } from '@y42/toast';
import { provideUserTrackingLegacy } from '@y42/user-tracking-legacy';
import { Y42FeatureFlag, Y42FeatureFlagModule } from '@y42/y42-feature-flag';
import { AngularSvgIconModule } from 'angular-svg-icon';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { AuthModule } from './auth/auth.module';
import { Y42ChatConfigService } from './core/chat/chat-config.service';
import { CoreModule } from './core/core.module';
import { Y42UserEnvironmentService } from './core/environment/environment.service';
import { Y42ListViewQuickFilterKeysService } from './core/list-view/y42-list-view-quick-filter-keys.service';
import { Y42PushNotificationConfigService } from './core/push-notification/push-notification-config.service';
import { Y42UserTrackingPreferencesServiceLegacy } from './core/user-tracking/user-tracking-preferences.service';
import { Y42DataLineageDiagramHydrationService } from './feature/data-lineage/data-lineage-diagram/services/data-lineage-diagram-hydration.service';
import { JobCreatorResolverService } from './feature/job/job-creator-resolver.service';
import { Y42FeatureChildAggregationService } from './feature/shared';
import { FeatureProvidersService } from './feature/shared/feature-provider.service';
import { provideAllDatasourceQueries } from './feature/shared/provide-all-datasources-query';
import { provideDatasourceTableJobServices } from './feature/shared/provide-datasource-table-job-services';
import { provideFeatureChildJobServices } from './feature/shared/provide-feature-child-job-services';
import { TriggerApiInterceptor } from './feature/shared/trigger-api.interceptor';
import { Y42FeatureAggregationService } from './feature/shared/y42-feature-aggregation.service';
import { GitDiffTreeService } from './git/git-diff-tree.service';
import { Y42GitModule } from './git/y42-git.module';
import { StatusIndicatorModule } from './shared/components/status-indicator/status-indicator.module';

@NgModule({
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    ToastModule.forRoot(),
    ErrorHandlerModule.forRoot(),
    CoreModule,
    AuthModule,
    AppRoutingModule,
    StatusIndicatorModule,
    MatButtonModule,
    FormsModule,
    ReactiveFormsModule,
    Y42FeatureFlagModule.forRoot(),
    HttpCacheInterceptorModule.forRoot(),
    AngularSvgIconModule.forRoot(),
    Y42GitModule,
  ],
  providers: [
    {
      provide: APP_UX,
      useValue: 'ux2',
    },
    provideEnvironment(Y42UserEnvironmentService),
    provideUserTrackingLegacy(Y42UserTrackingPreferencesServiceLegacy),
    // Mocking the UX3 user-tracking service here on purpose
    provideMockUserTrackingService(),
    providePushNotificationConfig(Y42PushNotificationConfigService),
    provideChatConfigServiceV1(Y42ChatConfigService), // todo: remove when getting rid of @y42/collaboration/chat
    provideGitDiffTree(GitDiffTreeService),
    {
      provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
      useValue: {
        showDelay: 150,
      } as MatTooltipDefaultOptions,
    },
    {
      provide: FeatureAggregationService,
      useClass: Y42FeatureAggregationService,
    },
    {
      provide: FeatureChildAggregationService,
      useClass: Y42FeatureChildAggregationService,
    },
    {
      provide: DataLineageDiagramHydrationService,
      useClass: Y42DataLineageDiagramHydrationService,
    },
    {
      provide: FeatureProvidersServiceInterface,
      useClass: FeatureProvidersService,
    },
    provideAllDatasourceQueries(),
    provideDatasourceTableJobServices(),
    provideFeatureChildJobServices(),
    provideListViewQuickFilterKeys(Y42ListViewQuickFilterKeysService),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TriggerApiInterceptor,
      multi: true,
    },
    {
      provide: AbstractJobCreatorResolverService,
      useClass: JobCreatorResolverService,
    },
  ],
  declarations: [AppComponent],
  bootstrap: [AppComponent],
})
export class AppModule {
  constructor(
    // instantiate
    readonly featureFlagService: FeatureFlagService<Y42FeatureFlag>,
    readonly userColorSchemeService: UserColorSchemeService,
  ) {}
}
